import React, { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from "react";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{
    outcome: "accepted" | "dismissed";
    platform: string;
  }>;
}

export type DeviceType = "ios" | "android" | "desktop";

export interface PWAContextValue {
  isInstalled: boolean;
  canInstall: boolean;
  deviceType: DeviceType;
  triggerInstall: () => Promise<boolean>;
  getInstructions: () => { title: string; steps: string[] };
}

const PWAContext = createContext<PWAContextValue | null>(null);

export function PWAProvider({ children }: { children: React.ReactNode }) {
  const deferredPromptRef = useRef<BeforeInstallPromptEvent | null>(null);
  const [canInstall, setCanInstall] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [deviceType, setDeviceType] = useState<DeviceType>("desktop");

  useEffect(() => {
    // Detect device type once
    const userAgent = navigator.userAgent.toLowerCase();
    console.log("[PWA] Initializing PWAContext, userAgent:", userAgent);
    
    if (/ipad|iphone|ipod/.test(userAgent) && !(window as any).MSStream) {
      setDeviceType("ios");
      console.log("[PWA] Device type: iOS");
    } else if (/android/.test(userAgent)) {
      setDeviceType("android");
      console.log("[PWA] Device type: Android");
    } else {
      setDeviceType("desktop");
      console.log("[PWA] Device type: Desktop");
    }

    // Capture the beforeinstallprompt event globally
    const handleBeforeInstallPrompt = (e: Event) => {
      console.log("[PWA] ✅ beforeinstallprompt event FIRED!");
      e.preventDefault();
      deferredPromptRef.current = e as BeforeInstallPromptEvent;
      setCanInstall(true);
      console.log("[PWA] Deferred prompt captured, canInstall set to true");
    };

    const handleAppInstalled = () => {
      console.log("[PWA] ✅ App installed event fired!");
      localStorage.setItem('pwa-installed', 'true');
      setIsInstalled(true);
      setCanInstall(false);
      deferredPromptRef.current = null;
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleAppInstalled);
    console.log("[PWA] Event listeners registered");

    // Detect if already installed (PWA standalone mode or localStorage)
    const isStandalone = window.matchMedia("(display-mode: standalone)").matches;
    const isIOSStandalone = (window.navigator as any).standalone;
    const wasInstalled = localStorage.getItem('pwa-installed') === 'true';
    console.log("[PWA] Standalone check - CSS:", isStandalone, "iOS:", isIOSStandalone, "localStorage:", wasInstalled);
    
    if (isStandalone || isIOSStandalone || wasInstalled) {
      setIsInstalled(true);
      console.log("[PWA] Already installed (standalone mode or localStorage)");
    }

    // Check service worker registration
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.getRegistrations().then((registrations) => {
        console.log("[PWA] Service worker registrations:", registrations.length);
        registrations.forEach((reg, i) => {
          console.log(`[PWA] SW ${i}: scope=${reg.scope}, state=${reg.active?.state}`);
        });
      });
    } else {
      console.log("[PWA] ❌ Service workers not supported");
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
      window.removeEventListener("appinstalled", handleAppInstalled);
    };
  }, []);

  const triggerInstall = useCallback(async (): Promise<boolean> => {
    console.log("[PWA] triggerInstall called, deferredPrompt exists:", !!deferredPromptRef.current);
    const prompt = deferredPromptRef.current;
    if (!prompt) {
      console.log("[PWA] ❌ No deferred prompt available");
      return false;
    }

    try {
      console.log("[PWA] Calling prompt()...");
      await prompt.prompt();
      const choice = await prompt.userChoice;
      console.log("[PWA] User choice:", choice.outcome);

      if (choice.outcome === "accepted") {
        deferredPromptRef.current = null;
        setCanInstall(false);
        return true;
      }
    } catch (error) {
      console.error("[PWA] Error during install:", error);
    }

    return false;
  }, []);

  const getInstructions = useCallback(() => {
    switch (deviceType) {
      case "ios":
        return {
          title: "settings.appInstallation.iosTitle",
          steps: [
            "settings.appInstallation.iosStep1",
            "settings.appInstallation.iosStep2",
            "settings.appInstallation.iosStep3",
          ],
        };
      case "android":
        return {
          title: "settings.appInstallation.androidTitle",
          steps: [
            "settings.appInstallation.androidStep1",
            "settings.appInstallation.androidStep2",
            "settings.appInstallation.androidStep3",
          ],
        };
      default:
        return {
          title: "settings.appInstallation.desktopTitle",
          steps: [
            "settings.appInstallation.desktopStep1",
            "settings.appInstallation.desktopStep2",
            "settings.appInstallation.desktopStep3",
          ],
        };
    }
  }, [deviceType]);

  const value = useMemo<PWAContextValue>(
    () => ({
      isInstalled,
      canInstall,
      deviceType,
      triggerInstall,
      getInstructions,
    }),
    [isInstalled, canInstall, deviceType, triggerInstall, getInstructions]
  );

  return <PWAContext.Provider value={value}>{children}</PWAContext.Provider>;
}

export function usePWAContext(): PWAContextValue {
  const context = useContext(PWAContext);
  if (!context) {
    throw new Error("usePWAContext must be used within a PWAProvider");
  }
  return context;
}
